import { Camera, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ModalShell from '@/components/modal-shell';
import { RangeField } from '@/components/ui/range-field';
import { api } from '@/lib/api';
import { AVATAR_SLUGS, type Profile } from '@/types/profile';

const V = 256; // avatar canvas size (px)
// Live in-app camera needs getUserMedia + a secure context (HTTPS/localhost). On plain HTTP
// (e.g. an offline SolarSPELL Pi served over the local network) it's blocked — there we fall back
// to a `capture` file input, which still opens the phone's native camera.
const liveCameraSupported =
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof window !== 'undefined' && window.isSecureContext;

interface Props {
  current: string;
  onSaved: (profile: Profile) => void;
  onClose: () => void;
}

export default function AvatarModal({ current, onSaved, onClose }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'select' | 'camera' | 'crop'>('select');
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const offset = useRef({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const captureRef = useRef<HTMLInputElement>(null);

  const baseScale = (img: HTMLImageElement) => V / Math.min(img.naturalWidth, img.naturalHeight);

  function clampOffset(img: HTMLImageElement, z: number) {
    const s = baseScale(img) * z;
    offset.current.x = Math.min(0, Math.max(V - img.naturalWidth * s, offset.current.x));
    offset.current.y = Math.min(0, Math.max(V - img.naturalHeight * s, offset.current.y));
  }

  function draw() {
    const c = canvasRef.current;
    if (!c || !image) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const s = baseScale(image) * zoom;
    ctx.clearRect(0, 0, V, V);
    ctx.drawImage(image, offset.current.x, offset.current.y, image.naturalWidth * s, image.naturalHeight * s);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { draw(); }, [image, zoom, mode]);

  useEffect(() => {
    if (mode === 'camera' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [mode]);
  useEffect(() => () => stopCamera(), []);

  function stopCamera() {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  }

  function loadImage(src: string) {
    const img = new Image();
    img.onload = () => {
      const s = baseScale(img);
      offset.current = { x: (V - img.naturalWidth * s) / 2, y: (V - img.naturalHeight * s) / 2 };
      setImage(img);
      setZoom(1);
      setMode('crop');
    };
    img.src = src;
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) loadImage(URL.createObjectURL(f));
  }

  async function startCamera() {
    setError('');
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      setMode('camera');
    } catch {
      setError(t('profile.avatar.cameraError'));
    }
  }

  function capturePhoto() {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const cv = document.createElement('canvas');
    cv.width = v.videoWidth;
    cv.height = v.videoHeight;
    cv.getContext('2d')?.drawImage(v, 0, 0, v.videoWidth, v.videoHeight);
    stopCamera();
    loadImage(cv.toDataURL('image/png'));
  }

  function changeZoom(z: number) {
    if (image) {
      const c = V / 2;
      const ratio = (baseScale(image) * z) / (baseScale(image) * zoom);
      offset.current.x = c - (c - offset.current.x) * ratio;
      offset.current.y = c - (c - offset.current.y) * ratio;
      clampOffset(image, z);
    }
    setZoom(z);
  }

  function onPointerDown(e: React.PointerEvent) { drag.current = { x: e.clientX, y: e.clientY }; e.currentTarget.setPointerCapture(e.pointerId); }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current || !image) return;
    offset.current.x += e.clientX - drag.current.x;
    offset.current.y += e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    clampOffset(image, zoom);
    draw();
  }
  function onPointerUp() { drag.current = null; }

  // Keyboard reposition (arrow keys) so the crop isn't pointer-only.
  function onCanvasKey(e: React.KeyboardEvent) {
    if (!image) return;
    const step = 16;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const m = moves[e.key];
    if (!m) return;
    e.preventDefault();
    offset.current.x += m[0];
    offset.current.y += m[1];
    clampOffset(image, zoom);
    draw();
  }

  async function selectPreset(slug: string) {
    setSaving(true);
    try {
      onSaved(await api.patch<Profile>('/profile/avatar', { avatar: slug }));
      onClose();
    } catch { setSaving(false); }
  }

  async function saveCrop() {
    const c = canvasRef.current;
    if (!c) return;
    setSaving(true);
    const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/webp', 0.9));
    if (!blob) { setSaving(false); return; }
    const fd = new FormData();
    fd.append('file', blob, 'avatar.webp');
    try {
      onSaved(await api.postForm<Profile>('/profile/avatar/upload', fd));
      onClose();
    } catch { setSaving(false); }
  }

  function backToSelect() { stopCamera(); setError(''); setMode('select'); }

  const outlineBtn = 'rounded-lg border border-ws-border bg-ws-btn px-5 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover';
  const primaryBtn = 'rounded-lg bg-ws-primary px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50';
  const sourceBtn = 'flex items-center justify-center gap-2 rounded-lg border border-dashed border-ws-border-strong bg-ws-bg px-4 py-3 text-sm font-medium text-ws-text hover:border-ws-primary hover:text-ws-primary';

  return (
    <ModalShell title={t('profile.avatar.title')} onClose={onClose}>
      {mode === 'select' && (
        <>
          <div className='grid grid-cols-2 gap-2.5'>
            <button type='button' onClick={() => uploadRef.current?.click()} className={sourceBtn}>
              <Upload className='h-4 w-4' />{t('profile.avatar.upload')}
            </button>
            {liveCameraSupported ? (
              <button type='button' onClick={startCamera} className={sourceBtn}>
                <Camera className='h-4 w-4' />{t('profile.avatar.takePhoto')}
              </button>
            ) : (
              <button type='button' onClick={() => captureRef.current?.click()} className={sourceBtn}>
                <Camera className='h-4 w-4' />{t('profile.avatar.takePhoto')}
              </button>
            )}
          </div>
          <input ref={uploadRef} type='file' accept='image/*' className='hidden' onChange={onFile} />
          {!liveCameraSupported && <input ref={captureRef} type='file' accept='image/*' capture='user' className='hidden' onChange={onFile} />}
          {error && <p role='alert' className='text-xs text-ws-error-fg'>{error}</p>}
          <div>
            <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-ws-muted'>{t('profile.avatar.choose')}</p>
            <div role='group' aria-label={t('profile.avatar.choose')} className='grid grid-cols-5 gap-2.5'>
              {AVATAR_SLUGS.map((slug) => (
                <button key={slug} onClick={() => selectPreset(slug)} disabled={saving} aria-label={slug} aria-pressed={current === slug}
                  className={`overflow-hidden rounded-full border-2 transition ${current === slug ? 'border-ws-primary' : 'border-transparent hover:border-ws-border-strong'}`}>
                  <img src={`${import.meta.env.BASE_URL}images/avatars/${slug}.png`} alt='' className='h-full w-full object-cover' />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {mode === 'camera' && (
        <div className='flex flex-col items-center gap-4'>
          <video ref={videoRef} playsInline muted aria-label={t('profile.avatar.cameraPreview')} className='h-48 w-48 rounded-full bg-ws-bg object-cover ring-2 ring-ws-border' />
          <div className='flex w-full items-center justify-between gap-3'>
            <button onClick={backToSelect} className={outlineBtn}>{t('profile.avatar.back')}</button>
            <button onClick={capturePhoto} className={primaryBtn}><span className='inline-flex items-center gap-1.5'><Camera className='h-4 w-4' />{t('profile.avatar.capture')}</span></button>
          </div>
        </div>
      )}

      {mode === 'crop' && (
        <div className='flex flex-col items-center gap-4'>
          <p id='crop-hint' className='text-sm text-ws-muted'>{t('profile.avatar.cropHint')}</p>
          <canvas ref={canvasRef} width={V} height={V} tabIndex={0} role='img' aria-label={t('profile.avatar.cropHint')} aria-describedby='crop-hint'
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onKeyDown={onCanvasKey}
            className='h-48 w-48 cursor-grab touch-none rounded-full bg-ws-bg ring-2 ring-ws-border focus:ring-ws-primary active:cursor-grabbing' />
          <RangeField
            label={t('profile.avatar.zoom')}
            value={zoom}
            onChange={changeZoom}
            min={1}
            max={3}
            step={0.01}
            className='w-full' />
          <div className='flex w-full items-center justify-between gap-3'>
            <button onClick={backToSelect} className={outlineBtn}>{t('profile.avatar.back')}</button>
            <button onClick={saveCrop} disabled={saving} className={primaryBtn}>{t('profile.avatar.usePhoto')}</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
